import { Rng } from '@/core/rng';

/**
 * Dungeon floor layouts.
 *
 * Rooms placed by rejection sampling, then joined in sequence by L-shaped
 * corridors. Connectivity is a property of the construction rather than
 * something checked afterwards: every room is joined to the one before it, so
 * the whole floor is one component and there can be no sealed-off treasure.
 *
 * Nothing here is saved. A floor is identified by the world seed and its
 * depth, and regenerated when entered — the same rule the weather follows.
 * That also means a layout can be reproduced exactly from two numbers, which
 * is what makes a bad floor reportable rather than a story.
 *
 * Deliberately not a BSP tree. BSP gives tidy space-filling layouts, which is
 * the wrong instinct here: rooms with gaps between them read as *places*, and
 * a corridor that runs somewhere is more interesting than one that merely
 * connects. It is also far easier to reason about when a floor comes out odd.
 */

export const WALL = 'M';
export const FLOOR = 'm';
export const STAIRS_DOWN = '>';
export const STAIRS_UP = '<';

/** Anything that can be stood on. Stairs are floor with a marking. */
export function isWalkable(char: string | undefined): boolean {
  return char === FLOOR || char === STAIRS_DOWN || char === STAIRS_UP;
}

export interface Room {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface Floor {
  readonly rows: string[];
  readonly rooms: Room[];
  /** Where the player arrives, in tiles. */
  readonly entrance: { x: number; y: number };
  /** Where the stairs down are, in tiles. */
  readonly exit: { x: number; y: number };
  readonly depth: number;
}

export interface FloorOptions {
  readonly seed: number;
  /** 1 is the first floor down. */
  readonly depth: number;
  readonly width?: number;
  readonly height?: number;
}

const DEFAULT_WIDTH = 48;
const DEFAULT_HEIGHT = 36;

/** Rooms never touch the outer wall, so a floor always has a solid border. */
const MARGIN = 2;

const MIN_ROOM = 4;
const MAX_ROOM = 9;

/** Tries per room before giving up. Overlap is common once a floor is busy. */
const PLACEMENT_ATTEMPTS = 40;

export function roomCountFor(depth: number): number {
  // Grows with depth, then flattens: a floor with thirty rooms is not harder
  // to survive, only longer to walk across.
  return Math.min(12, 5 + Math.floor(depth / 2));
}

export function centreOf(room: Room): { x: number; y: number } {
  return {
    x: room.x + Math.floor(room.width / 2),
    y: room.y + Math.floor(room.height / 2),
  };
}

function overlaps(a: Room, b: Room): boolean {
  // Compared with a tile of padding, so two rooms never share a wall — every
  // room stays a room rather than merging into one lumpy cavern.
  return (
    a.x - 1 < b.x + b.width &&
    a.x + a.width + 1 > b.x &&
    a.y - 1 < b.y + b.height &&
    a.y + a.height + 1 > b.y
  );
}

export function generateFloor({
  seed,
  depth,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
}: FloorOptions): Floor {
  const rng = Rng.forStream(seed, depth, 0x1);
  const grid: string[][] = Array.from({ length: height }, () =>
    Array.from({ length: width }, () => WALL),
  );

  const rooms: Room[] = [];
  const wanted = roomCountFor(depth);

  for (let i = 0; i < wanted; i++) {
    for (let attempt = 0; attempt < PLACEMENT_ATTEMPTS; attempt++) {
      const roomWidth = rng.int(MIN_ROOM, MAX_ROOM);
      const roomHeight = rng.int(MIN_ROOM, MAX_ROOM);
      const candidate: Room = {
        x: rng.int(MARGIN, width - roomWidth - MARGIN),
        y: rng.int(MARGIN, height - roomHeight - MARGIN),
        width: roomWidth,
        height: roomHeight,
      };

      if (rooms.some((room) => overlaps(room, candidate))) continue;

      rooms.push(candidate);
      carveRoom(grid, candidate);
      break;
    }
  }

  // Join each room to the previous one. Sequential rather than nearest-first:
  // it guarantees a single connected component in one pass, and the resulting
  // wandering corridors are more interesting than a minimum spanning tree.
  for (let i = 1; i < rooms.length; i++) {
    carveCorridor(grid, centreOf(rooms[i - 1]!), centreOf(rooms[i]!), rng);
  }

  const first = rooms[0];
  const last = rooms[rooms.length - 1];
  const entrance = first ? centreOf(first) : { x: 1, y: 1 };
  const exit = last ? centreOf(last) : { x: 1, y: 1 };

  // Written into the map rather than tracked beside it, so the way on is
  // something the player can see rather than a coordinate the code knows.
  if (rooms.length > 0) {
    grid[entrance.y]![entrance.x] = STAIRS_UP;
    grid[exit.y]![exit.x] = STAIRS_DOWN;
  }

  return {
    rows: grid.map((row) => row.join('')),
    rooms,
    entrance,
    exit,
    depth,
  };
}

function carveRoom(grid: string[][], room: Room): void {
  for (let y = room.y; y < room.y + room.height; y++) {
    for (let x = room.x; x < room.x + room.width; x++) {
      grid[y]![x] = FLOOR;
    }
  }
}

function carveCorridor(
  grid: string[][],
  from: { x: number; y: number },
  to: { x: number; y: number },
  rng: Rng,
): void {
  // Which leg runs first is a coin flip, so corridors do not all elbow the
  // same way and the floor stops looking machine-made.
  if (rng.chance(0.5)) {
    carveHorizontal(grid, from.x, to.x, from.y);
    carveVertical(grid, from.y, to.y, to.x);
  } else {
    carveVertical(grid, from.y, to.y, from.x);
    carveHorizontal(grid, from.x, to.x, to.y);
  }
}

function carveHorizontal(grid: string[][], fromX: number, toX: number, y: number): void {
  for (let x = Math.min(fromX, toX); x <= Math.max(fromX, toX); x++) {
    grid[y]![x] = FLOOR;
  }
}

function carveVertical(grid: string[][], fromY: number, toY: number, x: number): void {
  for (let y = Math.min(fromY, toY); y <= Math.max(fromY, toY); y++) {
    grid[y]![x] = FLOOR;
  }
}

/**
 * Every walkable tile reachable from a starting point.
 *
 * Exported because connectivity is the one property of a generated floor worth
 * proving rather than assuming — a floor whose stairs are sealed off is
 * unplayable, and it would happen rarely enough to reach players.
 */
export function reachableFrom(rows: readonly string[], start: { x: number; y: number }): Set<string> {
  const seen = new Set<string>();
  if (!isWalkable(rows[start.y]?.[start.x])) return seen;

  const queue = [start];
  seen.add(`${start.x},${start.y}`);

  while (queue.length > 0) {
    const { x, y } = queue.pop()!;
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const nextX = x + dx;
      const nextY = y + dy;
      const key = `${nextX},${nextY}`;
      if (seen.has(key)) continue;
      if (!isWalkable(rows[nextY]?.[nextX])) continue;
      seen.add(key);
      queue.push({ x: nextX, y: nextY });
    }
  }

  return seen;
}
